import { useEffect } from 'react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Loader2, LogIn } from 'lucide-react'
import { useAuth } from '../lib/auth/useAuth'
import { SESSION_QUERY_KEY } from '../lib/api/client'
import { RETURN_TO_KEY } from '../lib/constants'
import { Button } from '@/components/ui/button'
import { PageMain } from '@/components/page-main'

interface LoginSearch {
  redirect?: string
}

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
  beforeLoad: ({ context }) => {
    if (context.auth.isAuthenticated) {
      throw redirect({ to: '/dashboard' })
    }
  },
  component: LoginPage,
})

function LoginPage() {
  const { login, isLoading, error } = useAuth()
  const { redirect: returnTo } = Route.useSearch()
  const qc = useQueryClient()

  // Drop the previous user's queries so a login as a different account can't
  // flash their cached data. Session key is spared so the boot stays warm.
  useEffect(() => {
    qc.removeQueries({
      predicate: (q) => q.queryKey[0] !== SESSION_QUERY_KEY[0],
    })
  }, [qc])

  if (isLoading) {
    return (
      <PageMain className="flex min-h-screen items-center justify-center">
        <div role="status" aria-live="polite" className="flex flex-col items-center gap-3">
          <Loader2
            className="size-5 text-primary animate-spin"
            strokeWidth={1.5}
            aria-hidden
          />
          <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-muted-foreground">
            Verifying session
          </span>
        </div>
      </PageMain>
    )
  }

  function handleLogin() {
    if (returnTo) sessionStorage.setItem(RETURN_TO_KEY, returnTo)
    login()
  }

  return (
    <PageMain className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Atmospheric backdrop */}
      <div className="absolute inset-0 hawk-grid pointer-events-none" aria-hidden />
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 50% 30%, rgba(255, 77, 0, 0.10), transparent 70%), radial-gradient(ellipse 80% 50% at 50% 110%, rgba(4, 142, 255, 0.05), transparent 60%)',
        }}
      />
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 h-px w-[60%] pointer-events-none"
        aria-hidden
        style={{
          background:
            'linear-gradient(90deg, transparent, rgba(255, 77, 0, 0.6), transparent)',
        }}
      />

      {/* Brand top-left */}
      <div className="absolute top-6 left-6 font-mono text-[10px] uppercase tracking-[0.32em] text-muted-foreground flex items-center gap-4">
        <span className="text-foreground/80">Hawk</span>
        <span>Secure Registry</span>
      </div>
      <div className="absolute top-6 right-6 font-mono text-[10px] uppercase tracking-[0.32em] text-muted-foreground flex items-center gap-2">
        <span>v0.1</span>
        <span className="px-1.5 py-0.5 rounded bg-primary/15 text-primary tracking-wider">
          Beta
        </span>
      </div>

      <div className="relative w-full max-w-md px-6 animate-hawk-rise">
        {/* Wordmark */}
        <div className="text-center mb-12">
          <h1 className="text-[88px] leading-[0.85] font-medium tracking-[-0.04em] mb-2">
            hawk
            <span className="text-primary">.</span>
          </h1>
          <p className="text-sm text-muted-foreground max-w-xs mx-auto leading-relaxed">
            Secure package registry with policy-driven lifecycle management.
          </p>
        </div>

        {/* Card */}
        <div className="relative border border-border bg-card/80 backdrop-blur p-8 rounded-md">
          {/* Corner ticks */}
          <CornerTicks />

          {error && (
            <div className="mb-5 flex items-start gap-2 px-3 py-2.5 rounded-md bg-destructive/10 border border-destructive/30">
              <AlertCircle className="size-4 text-destructive shrink-0 mt-0.5" strokeWidth={1.5} />
              <p className="text-xs text-destructive flex-1 leading-relaxed">
                Unable to reach the backend. Is it running?
              </p>
            </div>
          )}

          <div className="space-y-5">
            <div className="space-y-1">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                Single Sign-On
              </span>
              <p className="text-sm text-foreground/80 leading-relaxed">
                Continue with your identity provider to access the registry.
              </p>
            </div>

            <Button
              onClick={handleLogin}
              className="w-full h-11 group relative overflow-hidden"
            >
              <span className="relative flex items-center gap-2 z-10">
                <LogIn className="size-4" />
                Continue with SSO
              </span>
            </Button>
          </div>
        </div>

        <div className="mt-8 text-center font-mono text-[10px] text-muted-foreground/60 tracking-wider">
          By signing in you accept the operator policy.
        </div>
      </div>

    </PageMain>
  )
}

function CornerTicks() {
  return (
    <>
      <span
        className="absolute -top-px -left-px w-3 h-3 border-t border-l border-primary/60"
        aria-hidden
      />
      <span
        className="absolute -top-px -right-px w-3 h-3 border-t border-r border-primary/60"
        aria-hidden
      />
      <span
        className="absolute -bottom-px -left-px w-3 h-3 border-b border-l border-primary/60"
        aria-hidden
      />
      <span
        className="absolute -bottom-px -right-px w-3 h-3 border-b border-r border-primary/60"
        aria-hidden
      />
    </>
  )
}
